import type { PostHogContextRef } from '../context'
import { buildInChatContextProperties } from '../context'

/**
 * Divination channel identifiers — preserved for callers that pass a
 * `DivinationChannel` value (`oracle_cards | tarot | horoscope`). Used as
 * a `context` (Layer 2) value via `PostHogEventContext`. No longer carried
 * as a per-event payload property — the experience identity is auto-attached
 * via the Layer 3 `chat_experience_name` instead.
 */
export type DivinationChannel = 'oracle_cards' | 'tarot' | 'horoscope'

/**
 * Chat experience lifecycle events. Fire when the user's current chat
 * experience transitions (entered on entry to the chat surface or on
 * change of `chat_experience_name`; exited on transition out or chat
 * module unmount).
 *
 * Replaces `ai_channel_join` / `ai_channel_exit`: those were
 * divination-channel-specific; the unified pair covers all chat
 * experiences (`Default`, `Tarot`, `Horoscope`, …) and identity lives
 * in the auto-attached Layer 3 `chat_experience_name`.
 */
export type ChannelEvents = {
  ai_experience_entered: () => void
  ai_experience_exited: () => void
}

export function createChannelEventCallbacks (
  contextRef: PostHogContextRef,
): ChannelEvents {
  return {
    ai_experience_entered: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_experience_entered', {
        ...buildInChatContextProperties(ctx),
      })
    },

    ai_experience_exited: () => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_experience_exited', {
        ...buildInChatContextProperties(ctx),
      })
    },
  }
}
